/**
 * Company Profile API Client
 *
 * Provides CRUD operations for company-specific node values
 * used in Company Info mode of the Business Graph Tab.
 */

import axios, { AxiosInstance } from 'axios'

// Default API base URL - matches omapApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

// Types for Company Profile API
export interface NodeValueResponse {
  company_id: number
  node_id: string
  node_value: string | null
  user_id: number | null
  last_updated: string | null
  node_metadata: {
    n_id: string
    n_name: string
    n_description: string | null
  } | null
}

export interface AllNodeValuesResponse {
  company_id: number
  values: Record<string, string>
}

export interface PopulatedNodesResponse {
  company_id: number
  node_ids: string[]
}

export interface SuccessResponse {
  status: string
  message: string | null
}

// Structured data types for special nodes
export interface CompanyInfoData {
  _format_version?: number
  company_name?: string
  street_address?: string
  city?: string
  state?: string
  zip_code?: string
  web_url?: string
  linkedin_url?: string
  revenue_tier?: string
  naics_code?: string
  note?: string
}

// IVP API response types
export interface NaicsResponse {
  company_id: number
  naics_code: string | null
  industry_label: string | null
  message?: string
  source?: string
}

export interface IVPVitalSign {
  name: string
  ontology_node: string | null
  normative_range: { p25: number; median: number; p75: number }
  unit: string
  source: string
  importance: 'critical' | 'high' | 'moderate'
  actual_value?: string
}

export interface IVPValueChainStep {
  node: string
  perspective: string
  role: string
}

export interface IVPPathology {
  name: string
  description: string
  ontology_cluster: string[]
  severity: 'critical' | 'high' | 'moderate'
  frequency: 'common' | 'occasional' | 'rare'
}

export interface IVPData {
  id: number
  naics_code: string
  revenue_tier: string
  industry_label: string
  profile_version: number
  status: string
  confidence_score: number | null
  vital_signs: { metrics: IVPVitalSign[] }
  value_chain: { critical_path: IVPValueChainStep[]; description: string }
  common_pathologies: { pathologies: IVPPathology[] }
  revenue_model: Record<string, any>
  cost_structure: Record<string, any>
  growth_levers: Record<string, any>
  env_sensitivities: Record<string, any>
}

export interface Employee {
  Full_name: string
  Title: string
  Process_primary: string
  Process_secondary: string
  LinkedIn_profile_URL: string
}

export interface Competitor {
  name: string
  address: string
  distance_mi: number
  rating: number
  reviews: number
  website: string
}

class CompanyProfileApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds
    })

    // Add request interceptor to attach JWT token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('jwt_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('jwt_token')
          window.location.href = 'http://127.0.0.1:5000'
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Get the value for a specific node for a company.
   */
  async getNodeValue(companyId: number, nodeId: string): Promise<NodeValueResponse> {
    const response = await this.client.get<NodeValueResponse>(
      `/company-profile/${companyId}/${nodeId}`
    )
    return response.data
  }

  /**
   * Save (create or update) a node value for a company.
   * If the value is empty, the record will be deleted.
   */
  async saveNodeValue(
    companyId: number,
    nodeId: string,
    value: string,
    userId: number = 1
  ): Promise<SuccessResponse> {
    const response = await this.client.put<SuccessResponse>(
      `/company-profile/${companyId}/${nodeId}`,
      { value, user_id: userId }
    )
    return response.data
  }

  /**
   * Delete a node value for a company.
   */
  async deleteNodeValue(companyId: number, nodeId: string): Promise<SuccessResponse> {
    const response = await this.client.delete<SuccessResponse>(
      `/company-profile/${companyId}/${nodeId}`
    )
    return response.data
  }

  /**
   * Get all node values for a company.
   */
  async getAllNodeValues(companyId: number): Promise<AllNodeValuesResponse> {
    const response = await this.client.get<AllNodeValuesResponse>(
      `/company-profile/${companyId}`
    )
    return response.data
  }

  /**
   * Get list of node IDs that have values for a company.
   * Useful for showing badges on nodes that have company data.
   */
  async getPopulatedNodes(companyId: number): Promise<PopulatedNodesResponse> {
    const response = await this.client.get<PopulatedNodesResponse>(
      `/company-profile/${companyId}/populated-nodes`
    )
    return response.data
  }

  // ==========================================================================
  // Helper methods for structured data
  // ==========================================================================

  /**
   * Parse company info JSON from node value.
   */
  parseCompanyInfo(nodeValue: string | null): CompanyInfoData {
    if (!nodeValue) return {}
    try {
      const parsed = JSON.parse(nodeValue)
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as CompanyInfoData
      }
      // Legacy text becomes note field
      return { note: nodeValue }
    } catch {
      // If not valid JSON, treat as legacy note
      return { note: nodeValue }
    }
  }

  /**
   * Serialize company info data to JSON string.
   */
  serializeCompanyInfo(data: CompanyInfoData): string {
    const obj: CompanyInfoData = { _format_version: 1 }

    if (data.company_name?.trim()) obj.company_name = data.company_name.trim()
    if (data.street_address?.trim()) obj.street_address = data.street_address.trim()
    if (data.city?.trim()) obj.city = data.city.trim()
    if (data.state?.trim()) obj.state = data.state.trim()
    if (data.zip_code?.trim()) obj.zip_code = data.zip_code.trim()
    if (data.web_url?.trim()) obj.web_url = data.web_url.trim()
    if (data.linkedin_url?.trim()) obj.linkedin_url = data.linkedin_url.trim()
    if (data.revenue_tier) obj.revenue_tier = data.revenue_tier
    if (data.naics_code?.trim()) obj.naics_code = data.naics_code.trim()
    if (data.note?.trim()) obj.note = data.note.trim()

    // If only format version, return empty
    if (Object.keys(obj).length <= 1) return ''

    return JSON.stringify(obj)
  }

  /**
   * Parse employees JSON from node value.
   */
  parseEmployees(nodeValue: string | null): Employee[] {
    if (!nodeValue) return []
    try {
      const parsed = JSON.parse(nodeValue)
      if (Array.isArray(parsed)) {
        return parsed as Employee[]
      }
      return []
    } catch {
      return []
    }
  }

  // ==========================================================================
  // NAICS & IVP API methods
  // ==========================================================================

  /**
   * Get resolved NAICS code for a company.
   */
  async getNaics(companyId: number): Promise<NaicsResponse> {
    const response = await this.client.get<NaicsResponse>(
      `/company-profile/${companyId}/naics`
    )
    return response.data
  }

  /**
   * Set NAICS code manually.
   */
  async setNaics(companyId: number, naicsCode: string): Promise<NaicsResponse> {
    const response = await this.client.put<NaicsResponse>(
      `/company-profile/${companyId}/naics`,
      { naics_code: naicsCode }
    )
    return response.data
  }

  /**
   * Trigger LLM-based NAICS derivation.
   */
  async deriveNaics(companyId: number): Promise<NaicsResponse> {
    const response = await this.client.post<NaicsResponse>(
      `/company-profile/${companyId}/naics/derive`
    )
    return response.data
  }

  /**
   * Get IVP for a company.
   */
  async getIVP(companyId: number): Promise<{ status: string; ivp: IVPData }> {
    const response = await this.client.get(`/ivp/${companyId}`)
    return response.data
  }

  /**
   * Trigger IVP generation for a company.
   */
  async generateIVP(companyId: number, naicsCode?: string, revenueTier?: string): Promise<{
    status: string
    message: string
    ivp_id: number
    ivp: IVPData
  }> {
    const response = await this.client.post(`/ivp/${companyId}/generate`, {
      naics_code: naicsCode || null,
      revenue_tier: revenueTier || null,
    })
    return response.data
  }

  /**
   * Get IVP vital signs with company actuals overlaid.
   */
  async getVitalSigns(companyId: number): Promise<{ status: string; vital_signs: { metrics: IVPVitalSign[]; ivp_id: number } }> {
    const response = await this.client.get(`/ivp/${companyId}/vital-signs`)
    return response.data
  }

  /**
   * Parse competitors JSON from node value.
   */
  parseCompetitors(nodeValue: string | null): Competitor[] {
    if (!nodeValue) return []
    try {
      const parsed = JSON.parse(nodeValue)
      if (Array.isArray(parsed)) {
        return parsed as Competitor[]
      }
      return []
    } catch {
      return []
    }
  }
}

// Export singleton instance
export const companyProfileApi = new CompanyProfileApiClient()
export default companyProfileApi
